import * as React from "react";
import { IconButton } from "../button";
import { Flex } from "../flex";
import { cx, mergeRefs } from "../../utils/dom";
import { useSpace } from "../space/space";
import { useNumberFieldState } from "react-stately";

import { Field, FieldComponentProps } from "../form/field";
import { AriaNumberFieldProps, useLocale, useNumberField } from "react-aria";

type NumberInputProps = AriaNumberFieldProps &
  FieldComponentProps<"input" | "decrement" | "increment"> & {
    showSteppers?: boolean;
    decrementIcon?: React.ReactNode;
    incrementIcon?: React.ReactNode;
  };

// TODO - add support for `append` and `prepend` props
// TODO - format stepper buttons
export function NumberInput({
  appearance = "outline",
  size = "md",
  color = "gray",
  classNames,
  styles,
  append,
  prepend,
  placeholder,
  showSteppers = false,
  error,
  label,
  minValue,
  maxValue,
  formatOptions,
  inputRef,
  incrementIcon,
  decrementIcon,
  onChange,
  ...props
}: NumberInputProps) {
  const ariaProps = {
    ...props,
    maxValue,
    formatOptions,
    minValue,
    onChange,
    "aria-label": props["aria-label"] ?? "number input",
  } as AriaNumberFieldProps;

  const space = useSpace();
  const internalInputRef = React.useRef<HTMLInputElement>(null);

  const { locale } = useLocale();
  const state = useNumberFieldState({ ...ariaProps, locale });
  const {
    errorMessageProps,
    descriptionProps,
    labelProps,
    groupProps,
    inputProps,
    incrementButtonProps,
    decrementButtonProps,
  } = useNumberField(ariaProps, state, internalInputRef);

  return (
    <Field
      classNames={classNames}
      color={color}
      size={size}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      errorMessageProps={errorMessageProps}
      error={error}
      label={label}
      replaceDefaultControlWrapper
      {...props}
    >
      <Flex gap="4xs" {...groupProps} className="p-input-container">
        {showSteppers && (
          <IconButton
            {...decrementButtonProps}
            radius="md"
            color={color}
            variant="outline"
            size={size}
            className={classNames?.decrement}
            style={styles?.decrement}
          >
            {decrementIcon}
          </IconButton>
        )}
        <input
          style={styles?.input}
          ref={mergeRefs(internalInputRef, inputRef)}
          placeholder={placeholder}
          className={cx(
            classNames?.input,
            "p-input-control",
            "space-item",
            space.classNames
          )}
          data-appearance={appearance}
          data-has-left-addon={!!prepend}
          data-has-right-addon={!!append}
          type="text"
          {...inputProps}
        />
        {showSteppers && (
          <IconButton
            {...incrementButtonProps}
            radius="md"
            color={color}
            variant="outline"
            size={size}
            className={classNames?.increment}
            style={styles?.increment}
          >
            {incrementIcon}
          </IconButton>
        )}
      </Flex>
    </Field>
  );
}
